import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const IPhoneNeustartSchleifePage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'iPhone startet ständig neu – Neustart-Schleife beheben | PhoneMedic',
      description: 'iPhone startet ständig neu? ✓ Neustart-Schleife & Boot-Loop beheben ✓ Software & Hardware-Ursachen ✓ Professionelle Reparatur ✓ 12 Monate Garantie ✓ Bundesweit | PhoneMedic',
      keywords: ['iPhone startet ständig neu', 'iPhone Neustart-Schleife', 'iPhone Boot-Loop', 'iPhone startet immer wieder neu', 'iPhone hängt beim Apple Logo', 'iPhone Reboot Loop'],
      canonicalUrl: 'https://www.phone-medic.de/iphone-neustart-schleife',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'iPhone Neustart-Schleife beheben',
        description: 'Professionelle Diagnose und Reparatur bei iPhone Boot-Loop und Neustart-Schleife. Software-Wiederherstellung bis Chip-Level Reparatur.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: { '@type': 'Offer', price: '49.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Warum startet mein iPhone ständig neu?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ursachen für eine iPhone Neustart-Schleife: fehlgeschlagenes iOS-Update, korrupte Systemdaten, defekter Akku (häufig!), Überhitzung, Wasserschaden oder ein Hardwaredefekt auf dem Logicboard.' },
          },
          {
            '@type': 'Question',
            name: 'Wie kann ich die iPhone Neustart-Schleife selbst beheben?',
            acceptedAnswer: { '@type': 'Answer', text: '1. Erzwungener Neustart versuchen. 2. iPhone per Finder/iTunes im Recovery-Modus wiederherstellen. 3. DFU-Wiederherstellung als letzter Software-Schritt. Hilft keiner dieser Schritte, liegt ein Hardwaredefekt vor.' },
          },
          {
            '@type': 'Question',
            name: 'Verliere ich meine Daten wenn ich die Neustart-Schleife behebe?',
            acceptedAnswer: { '@type': 'Answer', text: 'Bei einer Software-Wiederherstellung (Recovery oder DFU) werden alle Daten gelöscht. Gibt es ein iCloud-Backup, lassen sich die Daten danach wiederherstellen. Bei reinen Hardwarereparaturen (z.B. Akkutausch) bleiben Daten erhalten.' },
          },
          {
            '@type': 'Question',
            name: 'Kann ein schlechter Akku eine Neustart-Schleife verursachen?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ja, sehr häufig. Ein defekter Akku kann bei bestimmten Auslastungen einbrechen und einen Neustart auslösen. iOS erkennt das und startet neu – immer wieder. Ein Akkutausch löst dieses Problem oft vollständig.' },
          },
          {
            '@type': 'Question',
            name: 'iPhone hängt beim Apple Logo und startet nicht durch – was tun?',
            acceptedAnswer: { '@type': 'Answer', text: 'Das deutet auf ein Softwareproblem oder ein fehlgeschlagenes Update hin. Schließe das iPhone an iTunes/Finder an und versuche eine Recovery-Wiederherstellung. Klappt das nicht, ist DFU-Modus der nächste Schritt.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#iphone-neustart-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'iphone-neustart-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#iphone-neustart-schema')?.remove() }
  }, [])

  const causes = [
    { title: 'Defekter Akku', desc: 'Einer der häufigsten Gründe. Ein schwacher Akku bricht bei hoher Last ein, das iPhone startet neu – und wiederholt das in einer Schleife. Akkutausch löst das Problem meist sofort.' },
    { title: 'Fehlgeschlagenes iOS-Update', desc: 'Ein unterbrochenes oder fehlerhaftes Update kann das System in einem Zustand hinterlassen, aus dem es nicht mehr startet. Recovery-Modus oder DFU hilft.' },
    { title: 'Korrupte Systemdaten', desc: 'Beschädigte iOS-Dateien können den Boot-Prozess blockieren. Eine Systemwiederherstellung über iTunes/Finder behebt das in den meisten Fällen.' },
    { title: 'Überhitzung', desc: 'Erreicht das iPhone eine kritische Temperatur, erzwingt iOS einen Neustart als Schutzmaßnahme. Kühlt das Gerät sich nicht, startet die Schleife sofort wieder.' },
    { title: 'Wasserschaden', desc: 'Feuchtigkeit auf dem Logicboard kann Kurzschlüsse verursachen, die ständige Neustarts auslösen. Platinen-Reinigung und Reparatur der betroffenen Chips ist nötig.' },
    { title: 'Logicboard-Defekt', desc: 'In seltenen Fällen ist ein Chip auf dem Logicboard defekt. Das erfordert professionelle Chip-Level Diagnose unter dem Mikroskop.' },
  ]

  const softwareFixes = [
    { step: '1', title: 'Erzwungener Neustart', desc: 'Lautstärke + drücken, Lautstärke - drücken, dann Side Button halten bis Apple-Logo erscheint. Manchmal reicht das.' },
    { step: '2', title: 'Recovery-Modus', desc: 'iPhone per Kabel an Mac/PC anschließen, in Recovery-Modus versetzen, in Finder/iTunes „Wiederherstellen" wählen.' },
    { step: '3', title: 'DFU-Modus', desc: 'Der tiefste Software-Reset. Löscht alle Daten und installiert iOS neu. Letzter Software-Ausweg vor Hardware-Diagnose.' },
    { step: '4', title: 'Hardware-Diagnose', desc: 'Hilft kein Software-Reset, liegt ein Hardwaredefekt vor – oft der Akku. Professionelle Diagnose zeigt die genaue Ursache.' },
  ]

  const faqs = [
    { q: 'Warum startet mein iPhone ständig neu?', a: 'Häufige Ursachen: defekter Akku, fehlgeschlagenes iOS-Update, korrupte Systemdaten, Überhitzung, Wasserschaden oder Logicboard-Defekt.' },
    { q: 'Wie kann ich die Neustart-Schleife selbst beheben?', a: '1. Erzwungener Neustart. 2. Recovery-Modus in iTunes/Finder. 3. DFU-Wiederherstellung. Hilft keins davon → Hardwaredefekt → professionelle Reparatur.' },
    { q: 'Verliere ich meine Daten?', a: 'Bei Software-Wiederherstellung (Recovery/DFU) werden Daten gelöscht. Mit iCloud-Backup lassen sie sich danach wiederherstellen. Bei Hardwarereparatur (z.B. Akku) bleiben Daten erhalten.' },
    { q: 'Kann ein schlechter Akku eine Neustart-Schleife verursachen?', a: 'Ja, sehr häufig. Ein defekter Akku bricht bei Last ein und löst Neustarts aus. Akkutausch löst dieses Problem oft vollständig.' },
    { q: 'iPhone hängt beim Apple Logo – was tun?', a: 'Deutet auf ein Softwareproblem hin. Recovery-Wiederherstellung in iTunes/Finder probieren. Klappt das nicht → DFU-Modus.' },
    { q: 'Was kostet die Reparatur?', a: 'Akkutausch ab 49€ – löst sehr viele Neustart-Schleife Probleme. Bei Softwarefehlern günstige Wiederherstellung. Bei Hardwareschäden nach Diagnose. Immer erst Kostenvoranschlag.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              Boot-Loop · Neustart-Schleife · Software & Hardware
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              iPhone startet ständig neu –<br />
              <span className="text-indigo-400">Neustart-Schleife beheben</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              iPhone hängt beim Apple-Logo, startet immer wieder neu oder kommt nicht aus dem Boot-Loop? Wir finden die Ursache und reparieren es – oft ist es nur der Akku.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Reparatur anfragen
              </a>
              <a href="https://wa.me/491605010907" target="_blank" rel="noopener noreferrer" className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 49 €', label: 'Reparatur' },
              { number: 'Oft Akku', label: 'Häufigste Ursache' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'Bundesweit', label: 'Einsende-Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Warum startet das iPhone ständig neu?</h2>
            <p className="text-gray-500">Ursachen von einfach (Akku) bis komplex (Logicboard)</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {causes.map((c, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{c.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Schritt für Schritt selbst versuchen</h2>
            <p className="text-gray-500">Von einfach nach komplex – bevor du einschickst</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {softwareFixes.map((item, i) => (
              <div key={i} className="text-center flex flex-col items-center">
                <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-lg font-bold mb-4">{item.step}</div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
            <p className="text-amber-800 text-sm">
              <strong>Häufigste Lösung:</strong> In vielen Fällen ist ein defekter Akku die Ursache einer Neustart-Schleife. Ein Akkutausch ab 49 € löst das Problem oft vollständig – ohne Datenverlust.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere iPhone Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'iPhone Akku tauschen', path: '/iphone-akku-tauschen' },
              { label: 'iPhone Display Reparatur', path: '/iphone-display-reparatur' },
              { label: 'iPhone startet nicht', path: '/iphone-startet-nicht' },
              { label: 'Alle iPhone Reparaturen', path: '/repair' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">iPhone Neustart-Schleife? Wir beheben es.</h2>
          <p className="text-gray-400 mb-8">Oft nur Akku · ab 49 € · 12 Monate Garantie · Bundesweiter Einsende-Service</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Per E-Mail anfragen
            </a>
            <a href="https://wa.me/491605010907" target="_blank" rel="noopener noreferrer" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default IPhoneNeustartSchleifePage
